import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { FeedbackApiBase } from './feedback-api-base';
import {
  AddMockupRequest,
  AddPagesRequest,
  AddRoundAssetRequest,
  AssignMockupPagesRequest,
  CreateFeedbackRoundRequest,
  DataEnvelope,
  EmbedAllowedResponse,
  EnsurePageRequest,
  EnsurePageResult,
  FeedbackRound,
  FeedbackRoundAsset,
  FeedbackRoundStatus,
  FeedbackRoundUser,
  ListFeedbackRoundsQuery,
  ListRoundPagesQuery,
  MessageResultEnvelope,
  PageStatus,
  Paginated,
  RoundPages,
  StoreStakeholderRequest,
  SuccessEnvelope,
  TokenResponse,
  UpdateAllowedDomainsRequest,
  UpdatePageStatusRequest,
  UpdateStakeholderRoleRequest,
  ValidateRoundResponse,
} from '../models';

/**
 * Mirrors App\Http\Controllers\FeedbackRoundController.
 * All endpoints live under `/api/v1`.
 */
@Injectable({ providedIn: 'root' })
export class FeedbackRoundService extends FeedbackApiBase {
  /** GET /v1/token — issue a wildcard JWT. */
  getToken(): Observable<TokenResponse> {
    return this.http.get<TokenResponse>(this.url('token'));
  }

  /** POST /v1/feedbackrounds */
  createFeedbackRound(request: CreateFeedbackRoundRequest): Observable<FeedbackRound> {
    return this.http
      .post<SuccessEnvelope<FeedbackRound>>(this.url('feedbackrounds'), request)
      .pipe(map((response) => response.data));
  }

  /** GET /v1/validate/{orgId}/project/{projectId} */
  validateFeedbackRound(orgId: string, projectId: string): Observable<ValidateRoundResponse> {
    return this.http.get<ValidateRoundResponse>(
      this.url(`validate/${encodeURIComponent(orgId)}/project/${encodeURIComponent(projectId)}`),
    );
  }

  /** GET /v1/feedbackrounds */
  listFeedbackRounds(query: ListFeedbackRoundsQuery = {}): Observable<Paginated<FeedbackRound>> {
    return this.http
      .get<DataEnvelope<Paginated<FeedbackRound>>>(this.url('feedbackrounds'), {
        params: this.buildParams({ ...query }),
      })
      .pipe(map((response) => response.data));
  }

  /**
   * GET /v1/feedbackrounds/{id}
   * @param withFeedback When true, the round's feedback items are eager-loaded.
   */
  getFeedbackRound(id: string, withFeedback = false): Observable<FeedbackRound> {
    return this.http
      .get<SuccessEnvelope<FeedbackRound>>(this.url(`feedbackrounds/${id}`), {
        // Only send the param when true, so default calls stay paramless.
        params: this.buildParams({ withFeedback: withFeedback || undefined }),
      })
      .pipe(map((response) => response.data));
  }

  /**
   * GET /v1/feedbackrounds/{id}/embed-allowed
   * Whether `domain` is registered in the round's allowedDomains. Used by the
   * auth bridge to authorize releasing the token to the embedding origin.
   *
   * @param token Optional bearer token to authenticate this call with (e.g. the
   *   parent's validateround JWT in the bridge). When provided it is sent as an
   *   explicit Authorization header, which the auth interceptor leaves intact
   *   instead of substituting the stored token. Omit to use the stored token.
   */
  checkEmbedAllowed(feedbackRoundId: string, domain: string, token?: string): Observable<boolean> {
    return this.http
      .get<EmbedAllowedResponse>(this.url(`feedbackrounds/${feedbackRoundId}/embed-allowed`), {
        params: this.buildParams({ domain }),
        headers: token ? new HttpHeaders({ Authorization: `Bearer ${token}` }) : undefined,
      })
      .pipe(map((response) => response.allowed));
  }

  /** PUT /v1/feedbackrounds/{id}/status/{newStatus} */
  updateFeedbackRoundStatus(
    id: string,
    newStatus: FeedbackRoundStatus,
  ): Observable<FeedbackRound> {
    return this.http
      .put<SuccessEnvelope<FeedbackRound>>(this.url(`feedbackrounds/${id}/status/${newStatus}`), {})
      .pipe(map((response) => response.data));
  }

  /** PUT /v1/feedbackrounds/{id}/allowed-domains — replaces the embed domains. */
  updateAllowedDomains(id: string, allowedDomains: string[]): Observable<FeedbackRound> {
    const request: UpdateAllowedDomainsRequest = { allowedDomains };
    return this.http
      .put<SuccessEnvelope<FeedbackRound>>(this.url(`feedbackrounds/${id}/allowed-domains`), request)
      .pipe(map((response) => response.data));
  }

  /** PUT /v1/feedbackrounds/{id}/addmockup */
  addMockup(id: string, mockupUrls: string[]): Observable<FeedbackRound> {
    const request: AddMockupRequest = { mockupUrls };
    return this.http
      .put<SuccessEnvelope<FeedbackRound>>(this.url(`feedbackrounds/${id}/addmockup`), request)
      .pipe(map((response) => response.data));
  }

  /**
   * POST /v1/feedbackrounds/{id}/assets — adds a document (a link, or an uploaded
   * file's object URL) tagged copy / DRD / other. Resolves to the stored asset.
   */
  addAsset(id: string, request: AddRoundAssetRequest): Observable<FeedbackRoundAsset> {
    return this.http
      .post<SuccessEnvelope<FeedbackRoundAsset>>(this.url(`feedbackrounds/${id}/assets`), request)
      .pipe(map((response) => response.data));
  }

  /**
   * DELETE /v1/feedbackrounds/{id}/assets/{assetId} — takes an asset off the round
   * (an uploaded file stays stored). Resolves to the removed asset.
   */
  removeAsset(id: string, assetId: string): Observable<FeedbackRoundAsset> {
    return this.http
      .delete<SuccessEnvelope<FeedbackRoundAsset>>(this.url(`feedbackrounds/${id}/assets/${encodeURIComponent(assetId)}`))
      .pipe(map((response) => response.data));
  }

  /** PUT /v1/feedbackrounds/{id}/pages/add — bulk-adds pages (with optional mockups). */
  addPages(id: string, request: AddPagesRequest): Observable<FeedbackRound> {
    return this.http
      .put<SuccessEnvelope<FeedbackRound>>(this.url(`feedbackrounds/${id}/pages/add`), request)
      .pipe(map((response) => response.data));
  }

  /**
   * GET /v1/feedbackrounds/{id}/pages — one page of the round's pages (50 by
   * default), optionally filtered by URL text and status, with per-status counts
   * for the whole round.
   */
  listPages(id: string, query: ListRoundPagesQuery = {}): Observable<RoundPages> {
    return this.http
      .get<DataEnvelope<RoundPages>>(this.url(`feedbackrounds/${id}/pages`), {
        params: this.buildParams({ ...query }),
      })
      .pipe(map((response) => response.data));
  }

  /**
   * POST /v1/feedbackrounds/{id}/pages/ensure — the round's page for `url`, added as
   * Pending first if the round doesn't have it. Lets the widget track the page it's
   * on without downloading every page.
   */
  ensurePage(id: string, url: string): Observable<EnsurePageResult> {
    const request: EnsurePageRequest = { url };
    return this.http
      .post<SuccessEnvelope<EnsurePageResult>>(this.url(`feedbackrounds/${id}/pages/ensure`), request)
      .pipe(map((response) => response.data));
  }

  /** PUT /v1/feedbackrounds/{id}/mockup/assignpage */
  assignMockupPages(id: string, request: AssignMockupPagesRequest): Observable<FeedbackRound> {
    return this.http
      .put<SuccessEnvelope<FeedbackRound>>(
        this.url(`feedbackrounds/${id}/mockup/assignpage`),
        request,
      )
      .pipe(map((response) => response.data));
  }

  /** PUT /v1/feedbackrounds/{id}/page/status/{newStatus} — resolves to the result message. */
  updatePageStatus(id: string, newStatus: PageStatus, pageUrl: string): Observable<string> {
    const request: UpdatePageStatusRequest = { pageUrl };
    return this.http
      .put<MessageResultEnvelope>(this.url(`feedbackrounds/${id}/page/status/${newStatus}`), request)
      .pipe(map((response) => response.data.message));
  }

  /**
   * GET /v1/feedbackrounds/{id}/stakeholders — the round's Project Team.
   *
   * The API reconciles the list against the round's feedback and comment history on
   * every read, so rounds that predate stakeholder tracking still answer with their
   * real participants rather than an empty list.
   */
  listStakeholders(id: string): Observable<FeedbackRoundUser[]> {
    return this.http
      .get<DataEnvelope<FeedbackRoundUser[]>>(this.url(`feedbackrounds/${id}/stakeholders`))
      .pipe(map((response) => response.data));
  }

  /**
   * POST /v1/feedbackrounds/{id}/stakeholders — adds someone by hand.
   *
   * Resolves to the resulting list. Adding an address that is already listed is a
   * no-op rather than an error. A manual add also clears any previous removal of that
   * address, which is the only thing that does.
   */
  addStakeholder(id: string, request: StoreStakeholderRequest): Observable<FeedbackRoundUser[]> {
    return this.http
      .post<DataEnvelope<FeedbackRoundUser[]>>(
        this.url(`feedbackrounds/${id}/stakeholders`),
        request,
      )
      .pipe(map((response) => response.data));
  }

  /**
   * PATCH /v1/feedbackrounds/{id}/stakeholders — changes someone's role.
   *
   * Anyone may change anyone's role. Resolves to the resulting list; fails with 422
   * when `Owner` is asked for anyone but the owner.
   */
  updateStakeholderRole(
    id: string,
    request: UpdateStakeholderRoleRequest,
  ): Observable<FeedbackRoundUser[]> {
    return this.http
      .patch<DataEnvelope<FeedbackRoundUser[]>>(
        this.url(`feedbackrounds/${id}/stakeholders`),
        request,
      )
      .pipe(map((response) => response.data));
  }

  /**
   * DELETE /v1/feedbackrounds/{id}/stakeholders?email=… — removes someone. The owner
   * cannot be removed (409).
   *
   * Resolves to the resulting list. The removal is remembered, so the reconciliation
   * on the next read will not put them back even though their feedback and comments
   * are still on the round.
   */
  removeStakeholder(id: string, email: string): Observable<FeedbackRoundUser[]> {
    return this.http
      .delete<DataEnvelope<FeedbackRoundUser[]>>(this.url(`feedbackrounds/${id}/stakeholders`), {
        params: this.buildParams({ email }),
      })
      .pipe(map((response) => response.data));
  }
}
